import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Posts } from '../posts/posts';
import { Events } from '../events/events';
import { Blog } from '../blog/blog';
import { RouterLink } from '@angular/router';
import {
  HomeContentService,
  HomeSectionConfig,
  HomeSectionKey,
} from '../../shared/services/home-content.service';

// Used only if the layout request fails, so the page still renders the way it
// always did. Normally the super admin's settings come from the API.
const DEFAULT_HOME_SECTIONS: HomeSectionConfig[] = [
  { key: 'POSTS',  title: 'Explore Trending & Featured Posts', subtitle: '', isVisible: true, order: 1 },
  { key: 'EVENTS', title: 'Events near you',                   subtitle: '', isVisible: true, order: 2 },
  { key: 'BLOGS',  title: 'Blogs selected for you',            subtitle: '', isVisible: true, order: 3 },
];

// ── Shared carousel base ─────────────────────────────────────────────────────

interface CarouselBase {
  id: number;
  author: string;
  avatar: string;
  timeAgo: string;
  images: string[];
  likes: number;
  comments: number;
  shares: number;
  activeSlide?: number;
  intervalRef?: ReturnType<typeof setInterval>;
}

// ── Component ─────────────────────────────────────────────────────────────────



@Component({
  selector: 'app-home',
  imports: [CommonModule, Posts, Events, Blog, RouterLink],
  templateUrl: './home.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Home implements OnInit, AfterViewInit, OnDestroy {

  products: any[] = [
    {
      id: 1, badge: 'Bestseller', discount: 50,
      originalPrice: 50, salePrice: 25, title: 'Lorem Ipsum', merchant: 'From ACME Merchant company',
      rating: 5, reviewCount: 12,
      images: ['/images/product/product-2.jpeg', '/images/product/product-1.jpeg'],
      likes: 12, comments: 18, shares: 8, activeSlide: 0,
    },
    {
      id: 2, badge: 'New Launch',
      originalPrice: 50, salePrice: 25, title: 'Lorem Ipsum', merchant: 'From ACME Merchant company',
      rating: 5, reviewCount: 12,
      images: ['/images/product/product-3.jpeg'],
      likes: 12, comments: 18, shares: 8, activeSlide: 0,
    },
    {
      id: 3, badge: 'Bestseller', discount: 50,
      originalPrice: 50, salePrice: 25, title: 'Lorem Ipsum', merchant: 'From ACME Merchant company',
      rating: 5, reviewCount: 12,
      images: ['/images/product/product-4.jpeg', '/images/product/product-5.jpeg'],
      likes: 12, comments: 18, shares: 8, activeSlide: 0,
    },
    {
      id: 4, badge: 'Bestseller', discount: 50,
      originalPrice: 50, salePrice: 25, title: 'Lorem Ipsum', merchant: 'From ACME Merchant company',
      rating: 5, reviewCount: 12,
      images: ['/images/product/product-6.jpeg', '/images/product/product-7.jpeg', '/images/product/product-8.jpeg', '/images/product/product-9.jpeg'],
      likes: 12, comments: 18, shares: 8, activeSlide: 0,
    },
    {
      id: 5, badge: 'Bestseller',
      originalPrice: 50, salePrice: 25, title: 'Lorem Ipsum', merchant: 'From ACME Merchant company',
      rating: 5, reviewCount: 12,
      images: ['/images/product/product-10.jpeg', '/images/product/product-11.jpeg'],
      likes: 12, comments: 18, shares: 8, activeSlide: 0,
    },
    {
      id: 6, badge: 'Popular',
      originalPrice: 50, salePrice: 25, title: 'Lorem Ipsum', merchant: 'From ACME Merchant company',
      rating: 5, reviewCount: 12,
      images: ['/images/product/product-12.jpeg'],
      likes: 12, comments: 18, shares: 8, activeSlide: 0,
    },
    {
      id: 7, badge: 'Bestseller', discount: 50,
      originalPrice: 50, salePrice: 25, title: 'Lorem Ipsum', merchant: 'From ACME Merchant company',
      rating: 5, reviewCount: 12,
      images: ['/images/product/product-13.jpeg', '/images/product/product-14.jpeg', '/images/product/product-15.jpeg'],
      likes: 12, comments: 18, shares: 8, activeSlide: 0,
    },
    {
      id: 8, badge: 'Bestseller', discount: 50,
      originalPrice: 50, salePrice: 25, title: 'Lorem Ipsum', merchant: 'From ACME Merchant company',
      rating: 5, reviewCount: 12,
      images: ['/images/product/product-16.jpeg', '/images/product/product-17.jpeg', '/images/product/product-18.jpeg', '/images/product/product-19.jpeg'],
      likes: 12, comments: 18, shares: 8, activeSlide: 0,
    },
  ];

  faqs: any[] = [
    {
      id: 1,
      question: 'What is Safeword and how does it work?',
      answer: 'Safeword is a privacy-first platform designed to help you securely manage sensitive communications. It uses end-to-end encryption to ensure your data stays private and accessible only to you and your intended recipients.',
      isOpen: false
    },
    {
      id: 2,
      question: 'Is my data secure and who has access to it?',
      answer: 'Your data is encrypted at rest and in transit using industry-standard AES-256 encryption. Only you hold the keys — not even our team can access your information. We undergo regular third-party security audits to maintain the highest standards.',
      isOpen: false
    },
    {
      id: 3,
      question: 'Can I use Safeword across multiple devices?',
      answer: 'Yes, Safeword syncs seamlessly across all your devices — desktop, mobile, and tablet. Your encrypted vault is always up to date no matter where you sign in, with no manual syncing required.',
      isOpen: false
    },
    {
      id: 4,
      question: 'What happens if I forget my master password?',
      answer: 'Because of our zero-knowledge architecture, we cannot recover your master password on your behalf. We recommend setting up emergency access with a trusted contact and storing your recovery phrase somewhere safe when you first create your account.',
      isOpen: false
    },
    {
      id: 5,
      question: 'Is there a free plan available?',
      answer: 'Safeword offers a generous free tier that includes core features for personal use. For teams and advanced features like priority support, shared vaults, and audit logs, you can upgrade to a Pro or Business plan at any time.',
      isOpen: false
    },
    {
      id: 6,
      question: 'How do I cancel or change my subscription?',
      answer: "You can manage, change, or cancel your subscription at any time from your account settings. Cancellations take effect at the end of your current billing period, and you'll retain access to paid features until then. We don't charge any cancellation fees.",
      isOpen: false
    }
  ];

  /** Content sections managed by the super admin (Admin → Home Page Content):
   *  which ones show, in what order, with what heading. `null` until loaded. */
  homeSections: HomeSectionConfig[] | null = null;

  constructor(
    private cdr: ChangeDetectorRef,
    private homeContentService: HomeContentService,
  ) {}

  ngOnInit(): void {
    this.loadHomeSections();
  }

  private loadHomeSections(): void {
    this.homeContentService.getSections().subscribe({
      next: (res: any) => {
        const list: HomeSectionConfig[] = Array.isArray(res?.data) ? res.data : DEFAULT_HOME_SECTIONS;
        this.homeSections = list
          .filter(s => s.isVisible)
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        this.cdr.markForCheck();
      },
      error: () => {
        this.homeSections = DEFAULT_HOME_SECTIONS;
        this.cdr.markForCheck();
      },
    });
  }

  trackBySectionKey(_: number, section: HomeSectionConfig): HomeSectionKey {
    return section.key;
  }

  ngAfterViewInit(): void {}

  ngOnDestroy(): void {
    this.products.forEach((item: any) => {
      if (item.intervalRef) clearInterval(item.intervalRef);
    });
  }

  // ── Shared carousel logic (works for both FeedPost and EventPost) ──────────

  startCarousel(item: CarouselBase): void {
    if (item.images.length <= 1) return;
    item.intervalRef = setInterval(() => {
      item.activeSlide = ((item.activeSlide ?? 0) + 1) % item.images.length;
      this.cdr.markForCheck();
    }, 3000);
  }

  // Single goToSlide — works for BOTH posts and events (no type mismatch)
  goToSlide(item: CarouselBase, index: number): void {
    item.activeSlide = index;
    if (item.intervalRef) clearInterval(item.intervalRef);
    this.startCarousel(item);
    this.cdr.markForCheck();
  }

  toggle(item: any): void {
    const wasOpen = item.isOpen;
    // Close all
    this.faqs.forEach(f => f.isOpen = false);
    // Open clicked if it was closed
    if (!wasOpen) {
      item.isOpen = true;
    }
  }

  // ── Track helpers ──────────────────────────────────────────────────────────

  trackById(_: number, item: CarouselBase): number {
    return item.id;
  }

  trackByIndex(index: number): number {
    return index;
  }

    badgeClass(badge: any): any {
    switch (badge) {
      case 'Bestseller': return 'border-[#C8922A] text-[#C8922A]';
      case 'New Launch':  return 'border-[#2A9B72] text-[#2A9B72]';
      case 'Popular':     return 'border-[#9B2AB3] text-[#9B2AB3]';
    }
  }

  // ── Star array helper ──────────────────────────────────────────────────

  stars(count: number): number[] {
    return Array(count).fill(0);
  }
}